import React, { useMemo } from "react";
import * as THREE from "three";
import type { ThermalModelConfig, PanelData } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import { tempToHex } from "../../constants/thermalConstants";
import { THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS } from "./thermalHeatmapSubsystemConfig";
import { getSubsystemTemplate, DECK_TO_FACE_LABEL, NODE_PREFIX_TO_DECK } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/subsystems";

// ============================================================================
// GEOMETRY CACHE - Reuse geometries instead of creating new ones
// ============================================================================

const geometryCache = new Map<string, THREE.BufferGeometry>();

const getBoxGeometry = (width: number, height: number, depth: number): THREE.BufferGeometry => {
  const key = `box_${width}_${height}_${depth}`;
  if (!geometryCache.has(key)) {
    geometryCache.set(key, new THREE.BoxGeometry(width, height, depth));
  }
  return geometryCache.get(key)!;
};

const getBoxEdgesGeometry = (width: number, height: number, depth: number): THREE.BufferGeometry => {
  const key = `boxEdges_${width}_${height}_${depth}`;
  if (!geometryCache.has(key)) {
    const boxGeo = new THREE.BoxGeometry(width, height, depth);
    geometryCache.set(key, new THREE.EdgesGeometry(boxGeo));
  }
  return geometryCache.get(key)!;
};

const getCylinderGeometry = (radius: number, height: number, segments: number = 16): THREE.BufferGeometry => {
  const key = `cylinder_${radius}_${height}_${segments}`;
  if (!geometryCache.has(key)) {
    geometryCache.set(key, new THREE.CylinderGeometry(radius, radius, height, segments));
  }
  return geometryCache.get(key)!;
};

const getCylinderEdgesGeometry = (radius: number, height: number, segments: number = 16): THREE.BufferGeometry => {
  const key = `cylinderEdges_${radius}_${height}_${segments}`;
  if (!geometryCache.has(key)) {
    const cylGeo = new THREE.CylinderGeometry(radius, radius, height, segments);
    geometryCache.set(key, new THREE.EdgesGeometry(cylGeo));
  }
  return geometryCache.get(key)!;
};

// ============================================================================
// CONFIGURATION - Adjust these to change subsystem appearance
// ============================================================================

const CM_TO_HEATMAP = 0.01 * 0.18;     // Scale factor: cm → heatmap units (0.18 = 0.3 / 1.667)
const SUBSYSTEM_DEPTH_OFFSET = 0.03;    // Distance in front of face (Z axis after rotation)
const GRID_SIZE = 3;                    // Face divided into 3x3 grid
const CELL_SIZE = 1 / GRID_SIZE;        // Each cell is 1/3 of face size
const FILL_OPACITY = 0.3;               // Transparency of subsystem fill
const BORDER_COLOR = "#000000";         // Color of wireframe borders

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface SubsystemDef {
  id: string;
  label: string;
  position: [number, number, number];  // [x, y, z] in local coordinates
  size: [number, number, number];      // [width, height, depth] in heatmap units
  nodeIds: string[];                   // Heatmap node IDs for temperature
  shape?: "box" | "cylinder";         // Geometry shape (defaults to "box")
  rotation?: [number, number, number]; // [x, y, z] rotation in radians
}

interface ThermalSubsystemRendererProps {
  deckKey: "PX" | "INT1" | "INT2" | "NX";
  currentRow: Record<string, number> | null;
  globalMin: number;
  globalMax: number;
  thermalModel?: ThermalModelConfig;
}

// ============================================================================
// SUBSYSTEM CONVERSION
// ============================================================================

const mapDeckKeyToPanelLabel = (deckKey: "PX" | "INT1" | "INT2" | "NX"): string => {
  return DECK_TO_FACE_LABEL[NODE_PREFIX_TO_DECK[deckKey]];
};

const convertThermalModelSubsystems = (
  panels: PanelData[],
  deckKey: "PX" | "INT1" | "INT2" | "NX"
): SubsystemDef[] => {
  const targetLabel = mapDeckKeyToPanelLabel(deckKey);
  const targetPanel = panels.find(p => p.panelLabel === targetLabel);
  if (!targetPanel) return [];

  const result: SubsystemDef[] = [];
  let subIndex = 0;

  targetPanel.cells.forEach((cell) => {
    cell.subsystems.forEach((subsystem) => {
      const template = getSubsystemTemplate(subsystem.subsystem as any);
      const gridPos = cell.localIndex;
      const nodeId = `${deckKey}_N${gridPos + 1}`;
      
      const row = Math.floor(gridPos / GRID_SIZE);
      const col = gridPos % GRID_SIZE;
      
      let x = (col - (GRID_SIZE - 1) / 2) * CELL_SIZE + (subsystem.positionOffset?.x || 0) * CM_TO_HEATMAP;
      let y = ((GRID_SIZE - 1) / 2 - row) * CELL_SIZE + (subsystem.positionOffset?.z || 0) * CM_TO_HEATMAP;
      
      if (deckKey === "PX") {
        y = -y;
      }
      
      const z = SUBSYSTEM_DEPTH_OFFSET + (subsystem.positionOffset?.y || 0) * CM_TO_HEATMAP;
      
      const width = (template.defaultDimensions.width) * CM_TO_HEATMAP;
      const height = (template.defaultDimensions.length) * CM_TO_HEATMAP;
      const depth = (template.defaultDimensions.height) * CM_TO_HEATMAP;
      
      const rotation = subsystem.rotation ? [
        (subsystem.rotation.x || 0) * (Math.PI / 180),
        (subsystem.rotation.y || 0) * (Math.PI / 180),
        (subsystem.rotation.z || 0) * (Math.PI / 180),
      ] as [number, number, number] : undefined;
      
      result.push({
        id: `${deckKey.toLowerCase()}-${subsystem.subsystem}-${subIndex}`,
        label: subsystem.displayName,
        position: [x, y, z],
        size: [width, height, depth],
        nodeIds: [nodeId],
        rotation,
      });
      subIndex++;
    });
  });

  return result;
};

/**
 * Convert ThermalDesigner subsystem placements to heatmap format
 * 
 * NODE ID MAPPING:
 * - ThermalDesigner: "1-0", "1-2", "1-6", "1-8" (face "1" = +X, position 0-8)
 * - Heatmap: "PX_N1", "PX_N3", "PX_N7", "PX_N9" (add 1 to position)
 * 
 * POSITION CALCULATION:
 * - Grid position (0-8) → 2D coords (row, col)
 * - row = floor(pos / 3), col = pos % 3
 * - x = (col - 1) * cellSize + positionOffset.x * scale  (horizontal: left ← → right)
 * - y = (1 - row) * cellSize + positionOffset.z * scale  (vertical on face: bottom ↓ ↑ top)
 * - z = depth offset + positionOffset.y * scale          (depth: into face ← → out from face)
 * 
 * DIMENSION MAPPING (after parent rotation [π/2, 0, 0]):
 * - template.width  → X (horizontal)
 * - template.length → Y (vertical on face, was Z before rotation)
 * - template.height → Z (depth from face, was Y before rotation)
 */
const getSubsystemsForDeck = (deckKey: "PX" | "INT1" | "INT2" | "NX"): SubsystemDef[] => {
  const targetDeck = NODE_PREFIX_TO_DECK[deckKey];
  
  return THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS
    .filter(placement => placement.deck === targetDeck)
    .map((placement, index) => {
      const template = getSubsystemTemplate(placement.type);
      
      // Extract grid position from node ID (e.g., "1-8" → 8)
      const gridPos = parseInt(placement.nodeId.split('-')[1], 10);
      
      // Map to heatmap node ID (e.g., gridPos 8 → "PX_N9")
      const nodeId = `${deckKey}_N${gridPos + 1}`;
      
      // Calculate 2D grid coordinates (0-8 → row 0-2, col 0-2)
      const row = Math.floor(gridPos / GRID_SIZE);
      const col = gridPos % GRID_SIZE;
      
      // Convert to face coordinates (centered, with offsets from placement)
      let x = (col - (GRID_SIZE - 1) / 2) * CELL_SIZE + (placement.positionOffset?.x || 0) * CM_TO_HEATMAP;
      let y = ((GRID_SIZE - 1) / 2 - row) * CELL_SIZE + (placement.positionOffset?.z || 0) * CM_TO_HEATMAP;
      
      // Flip Y coordinate for PX external face to match node layout
      if (deckKey === "PX") {
        y = -y;
      }
      
      const z = SUBSYSTEM_DEPTH_OFFSET + (placement.positionOffset?.y || 0) * CM_TO_HEATMAP;
      
      // Convert dimensions (swap to account for parent rotation)
      // Apply dimension overrides if provided
      const width = (placement.dimensionOverride?.width ?? template.defaultDimensions.width) * CM_TO_HEATMAP;
      const height = (placement.dimensionOverride?.length ?? template.defaultDimensions.length) * CM_TO_HEATMAP;   // length becomes height
      const depth = (placement.dimensionOverride?.height ?? template.defaultDimensions.height) * CM_TO_HEATMAP;    // height becomes depth
      
      // Convert rotation from degrees to radians
      const rotation = placement.rotation ? [
        (placement.rotation.x || 0) * (Math.PI / 180),
        (placement.rotation.y || 0) * (Math.PI / 180),
        (placement.rotation.z || 0) * (Math.PI / 180),
      ] as [number, number, number] : undefined;
      
      return {
        id: `${deckKey.toLowerCase()}-${placement.type}-${index}`,
        label: placement.label,
        position: [x, y, z],
        size: [width, height, depth],
        nodeIds: [nodeId],
        shape: placement.shape || "box",
        rotation,
      };
    });
};

// ============================================================================
// COMPONENT
// ============================================================================

const ThermalSubsystemRenderer: React.FC<ThermalSubsystemRendererProps> = React.memo(({
  deckKey,
  currentRow,
  globalMin,
  globalMax,
  thermalModel,
}) => {
  const subsystems = useMemo(() => {
    if (thermalModel?.panels) {
      return convertThermalModelSubsystems(thermalModel.panels, deckKey);
    }
    return getSubsystemsForDeck(deckKey);
  }, [deckKey, thermalModel]);

  const subsystemRenderData = useMemo(() => {
    if (!currentRow) return [];

    const result: Array<{ sub: SubsystemDef; temp: number; color: string }> = [];
    subsystems.forEach((sub) => {
      let sum = 0;
      let count = 0;

      sub.nodeIds.forEach((nodeId) => {
        const temp = currentRow[nodeId];
        if (temp !== undefined) {
          sum += temp;
          count++;
        }
      });

      if (count > 0) {
        const temp = sum / count;
        result.push({ sub, temp, color: tempToHex(temp, globalMin, globalMax) });
      }
    });
    return result;
  }, [currentRow, subsystems, globalMin, globalMax]);

  // Render subsystems
  return (
    <group name={`subsystems-${deckKey}`}>
      {subsystemRenderData.map(({ sub, temp, color }) => {

        const isCylinder = sub.shape === "cylinder";
        const [width, height, depth] = sub.size;
        const cylinderRadius = Math.min(width, height) / 2;
        const cylinderHeight = depth;

        return (
          <SubsystemMesh
            key={sub.id}
            sub={sub}
            temp={temp}
            color={color}
            isCylinder={isCylinder}
            width={width}
            height={height}
            depth={depth}
            cylinderRadius={cylinderRadius}
            cylinderHeight={cylinderHeight}
          />
        );
      })}

    </group>
  );
});

// ============================================================================
// MEMOIZED SUBSYSTEM MESH COMPONENT
// ============================================================================

interface SubsystemMeshProps {
  sub: SubsystemDef;
  temp: number;
  color: string;
  isCylinder: boolean;
  width: number;
  height: number;
  depth: number;
  cylinderRadius: number;
  cylinderHeight: number;
}

const SubsystemMesh = React.memo<SubsystemMeshProps>(({ 
  sub, 
  temp, 
  color, 
  isCylinder, 
  width, 
  height, 
  depth,
  cylinderRadius,
  cylinderHeight
}) => {

  // Get cached geometries
  const fillGeometry = useMemo(() => {
    return isCylinder 
      ? getCylinderGeometry(cylinderRadius, cylinderHeight, 16)
      : getBoxGeometry(width, height, depth);
  }, [isCylinder, width, height, depth, cylinderRadius, cylinderHeight]);

  const edgesGeometry = useMemo(() => {
    return isCylinder
      ? getCylinderEdgesGeometry(cylinderRadius, cylinderHeight, 16)
      : getBoxEdgesGeometry(width, height, depth);
  }, [isCylinder, width, height, depth, cylinderRadius, cylinderHeight]);

  return (
    <group position={sub.position} rotation={sub.rotation}>
      {/* Semi-transparent colored fill */}
      <mesh geometry={fillGeometry}>
        <meshBasicMaterial color={color} transparent opacity={FILL_OPACITY} side={THREE.DoubleSide} />
      </mesh>
      
      {/* Black wireframe border */}
      <lineSegments geometry={edgesGeometry}>
        <lineBasicMaterial color={BORDER_COLOR} linewidth={2} />
      </lineSegments>
    </group>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for optimization - only re-render if these change
  return (
    prevProps.sub.id === nextProps.sub.id &&
    prevProps.temp === nextProps.temp &&
    prevProps.color === nextProps.color &&
    prevProps.isCylinder === nextProps.isCylinder &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.depth === nextProps.depth
  );
});

SubsystemMesh.displayName = 'SubsystemMesh';

export default ThermalSubsystemRenderer;
